'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

interface UserProfile {
  role: string;
  full_name: string;
  company_id: string | null;
  company_name: string;
}

export type PermissionLevel = 'Write' | 'Read' | 'None';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const ALL_MODULES = [
  // Empresas
  'Empresas',
  // Personal
  'Personal',
  'Personal - Horas Extra',
  'Personal - Permisos',
  'Personal - Vacaciones',
  'Personal - Terminación',
  'Personal - Checklist Salida',
  'Personal - Actas Admin',
  'Personal - HR Reports',
  // Proyectos
  'Proyectos',
  'Proyectos - Repositorios',
  'Proyectos - Costos',
  'Proyectos - Tiempos',
  'Proyectos - Personal',
  'Proyectos - Wizard',
  // Infraestructura
  'Infraestructura',
  // Git
  'Git',
  'Git - Analíticas',
  'Git - Alertas',
  // Reuniones
  'Reuniones',
  'Reuniones - Minutas',
  'Reuniones - Plantillas',
  'Reuniones - Analítica',
  // Compras
  'Compras',
  'Compras - Analítica',
  // Análisis Financiero
  'Análisis Financiero',
  'Análisis Financiero - Configuración',
  'Análisis Financiero - Reportes',
  // Reportes & Analítica
  'Reportes',
  'Analítica',
  // Configuración
  'Configuración',
];

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<Record<string, PermissionLevel>>({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const supabase = createClient();

  const fetchPermissions = useCallback(async (role: string) => {
    if (!role) return;
    setPermissionsLoading(true);
    try {
      // Use maybeSingle() to avoid Supabase "multiple rows" warnings
      const { data: roleData } = await supabase
        .from('custom_roles')
        .select('id')
        .or(`system_role.eq.${role},name.eq.${role}`)
        .maybeSingle();

      if (!roleData?.id) {
        if (role === 'SUPER_ADMIN') {
          const map: Record<string, PermissionLevel> = {};
          ALL_MODULES.forEach((m) => { map[m] = 'Write'; });
          setPermissions(map);
        }
        return;
      }

      const { data: permsData } = await supabase
        .from('role_permissions')
        .select('module, permission_level')
        .eq('role_id', roleData.id);

      if (permsData) {
        const map: Record<string, PermissionLevel> = {};
        permsData.forEach((p: any) => {
          map[p.module] = p.permission_level as PermissionLevel;
        });
        setPermissions(map);
      }
    } catch {
      if (role === 'SUPER_ADMIN') {
        const map: Record<string, PermissionLevel> = {};
        ALL_MODULES.forEach((m) => { map[m] = 'Write'; });
        setPermissions(map);
      }
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data: prof } = await supabase
        .from('profiles')
        .select('role, full_name')
        .eq('id', userId)
        .maybeSingle();

      // Use maybeSingle() to avoid warning when user has no company
      const { data: member } = await supabase
        .from('company_members')
        .select('company_id, companies(name)')
        .eq('user_id', userId)
        .eq('is_primary_company', true)
        .maybeSingle();

      const profileData: UserProfile = {
        role: prof?.role || 'DEVELOPER',
        full_name: prof?.full_name || '',
        company_id: member?.company_id || null,
        company_name: (member as any)?.companies?.name || 'Mi Empresa',
      };
      setProfile(profileData);
      // Fetch permissions for this role
      await fetchPermissions(profileData.role);
      return profileData;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Use getUser() for server-validated auth state (more reliable than getSession)
        const { data: { user: currentUser }, error } = await supabase.auth.getUser();
        if (error) {
          const msg = (error as any)?.message || '';
          const code = (error as any)?.code || '';
          if (
            msg.includes('Refresh Token Not Found') ||
            msg.includes('Invalid Refresh Token') ||
            msg.includes('refresh_token_not_found') ||
            code === 'session_expired' ||
            code === 'refresh_token_not_found' ||
            code === 'user_not_found'
          ) {
            await supabase.auth.signOut().catch(() => {});
            setSession(null);
            setUser(null);
            setLoading(false);
            if (typeof window !== 'undefined') {
              window.location.href = '/login';
            }
            return;
          }
          setLoading(false);
          return;
        }
        if (currentUser) {
          // Also get the session for the session state
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          setSession(currentSession);
          setUser(currentUser);
          await fetchProfile(currentUser.id);
        }
      } catch {
        // Silent fail — onAuthStateChange will handle state
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (
        (_event === 'TOKEN_REFRESHED' && !session) ||
        _event === 'SIGNED_OUT'
      ) {
        supabase.auth.signOut().catch(() => {});
        setSession(null);
        setUser(null);
        setProfile(null);
        setPermissions({});
        setLoading(false);
        if (
          typeof window !== 'undefined' &&
          !window.location.pathname.startsWith('/login') &&
          !window.location.pathname.startsWith('/forgot-password') &&
          !window.location.pathname.startsWith('/reset-password')
        ) {
          window.location.href = '/login';
        }
        return;
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setProfile(null);
        setPermissions({});
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || ''
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    if (data.user) await fetchProfile(data.user.id);
    return data;
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // Ignore sign-out errors (e.g. token already invalid)
    }
    setProfile(null);
    setSession(null);
    setUser(null);
    setPermissions({});
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const getUserRole = async () => {
    if (!user) return null;
    if (profile?.role) return profile.role;
    const { data } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', user.id)
      .maybeSingle();
    return data?.role || null;
  };

  const canWrite = (module: string): boolean => permissions[module] === 'Write';
  const canRead = (module: string): boolean =>
    permissions[module] === 'Write' || permissions[module] === 'Read';
  const hasAccess = (module: string): boolean =>
    permissions[module] === 'Write' || permissions[module] === 'Read';

  const value = {
    user,
    session,
    loading,
    profile,
    permissions,
    permissionsLoading,
    canWrite,
    canRead,
    hasAccess,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    getUserRole,
    refreshProfile: () => user ? fetchProfile(user.id) : Promise.resolve(null),
    refreshPermissions: () => profile?.role ? fetchPermissions(profile.role) : Promise.resolve(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
